import React, { useEffect, useState } from 'react';
import { getDashboard, getAdminDashboard } from '../api/adminApi';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import Badge from '../components/ui/Badge';
import {
  Gamepad2,
  TrendingUp,
  DollarSign,
  CheckCircle,
  Users,
  Shield,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const styles = {
  header: {
    marginBottom: 'var(--space-xl)',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-md)',
  },
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: '1.75rem',
    fontWeight: 700,
    color: 'var(--color-text-primary)',
    margin: 0,
    letterSpacing: '0.03em',
  },
  subtitle: {
    fontSize: '0.9rem',
    color: 'var(--color-text-muted)',
    margin: '4px 0 0',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 'var(--space-lg)',
  },
  statCard: {
    backgroundColor: 'var(--color-bg-card)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid rgba(212, 175, 55, 0.1)',
    padding: 'var(--space-lg)',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'var(--space-md)',
    transition: 'all var(--transition-normal)',
  },
  statIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    borderRadius: 'var(--radius-md)',
    flexShrink: 0,
  },
  statContent: {
    flex: 1,
  },
  statLabel: {
    fontSize: '0.75rem',
    fontFamily: 'var(--font-display)',
    fontWeight: 500,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--color-text-muted)',
    margin: 0,
  },
  statValue: {
    fontSize: '1.75rem',
    fontFamily: 'var(--font-display)',
    fontWeight: 700,
    color: 'var(--color-text-primary)',
    margin: 0,
    lineHeight: 1.2,
  },
  section: {
    marginTop: 'var(--space-xl)',
  },
  sectionTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: '1.1rem',
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
    margin: '0 0 var(--space-md)',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 'var(--space-lg)',
  },
  summaryCard: {
    backgroundColor: 'var(--color-bg-card)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid rgba(212, 175, 55, 0.1)',
    padding: 'var(--space-lg)',
  },
  summaryLabel: {
    fontSize: '0.8rem',
    fontFamily: 'var(--font-display)',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: 'var(--color-text-muted)',
    margin: '0 0 var(--space-sm)',
  },
  summaryValue: {
    fontSize: '1.5rem',
    fontFamily: 'var(--font-display)',
    fontWeight: 700,
    color: 'var(--color-primary)',
    margin: 0,
  },
  summaryDetail: {
    fontSize: '0.8rem',
    color: 'var(--color-text-muted)',
    margin: 'var(--space-xs) 0 0',
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
    padding: 'var(--space-2xl)',
  },
  error: {
    padding: 'var(--space-lg)',
    backgroundColor: 'rgba(231, 76, 60, 0.1)',
    border: '1px solid rgba(231, 76, 60, 0.2)',
    borderRadius: 'var(--radius-md)',
    color: 'var(--color-danger)',
    fontSize: '0.9rem',
    textAlign: 'center',
  },
  adminCard: {
    backgroundColor: 'var(--color-bg-card)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid rgba(212, 175, 55, 0.1)',
    padding: 'var(--space-lg)',
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-md)',
    textDecoration: 'none',
    transition: 'all var(--transition-normal)',
    cursor: 'pointer',
  },
  adminCardIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'rgba(52, 152, 219, 0.1)',
    color: 'var(--color-info)',
    flexShrink: 0,
  },
  adminCardContent: {
    flex: 1,
  },
  adminCardTitle: {
    fontSize: '1rem',
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    color: 'var(--color-text-primary)',
    margin: 0,
  },
  adminCardDesc: {
    fontSize: '0.8rem',
    color: 'var(--color-text-muted)',
    margin: '2px 0 0',
  },
};

const statCards = [
  {
    key: 'total_games',
    label: 'Partite totali',
    icon: Gamepad2,
    color: 'var(--color-primary)',
    bg: 'rgba(212, 175, 55, 0.1)',
  },
  {
    key: 'completed_games',
    label: 'Partite completate',
    icon: CheckCircle,
    color: 'var(--color-success)',
    bg: 'rgba(46, 204, 113, 0.1)',
  },
  {
    key: 'active_games',
    label: 'Partite in corso',
    icon: TrendingUp,
    color: 'var(--color-info)',
    bg: 'rgba(52, 152, 219, 0.1)',
  },
  {
    key: 'average_prize',
    label: 'Premio medio',
    icon: DollarSign,
    color: 'var(--color-warning)',
    bg: 'rgba(243, 156, 18, 0.1)',
    format: 'currency',
  },
];

function formatValue(value, format) {
  if (value == null) return '0';
  if (format === 'currency') {
    return `\u20AC ${Number(value).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }
  return typeof value === 'number' ? value.toLocaleString('it-IT') : String(value);
}

export default function AdminDashboard() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [adminData, setAdminData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        // Always load operator dashboard (own stats)
        const result = await getDashboard();
        setData(result);

        // If admin, also load global dashboard
        if (isAdmin) {
          try {
            const globalResult = await getAdminDashboard();
            setAdminData(globalResult);
          } catch {
            // Admin dashboard may fail, that's ok
          }
        }
      } catch (err) {
        setError('Errore nel caricamento della dashboard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isAdmin]);

  if (loading) {
    return (
      <div style={styles.centered}>
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <div style={styles.error}>{error}</div>;
  }

  const stats = data || {};
  const globalStats = adminData || {};

  return (
    <div>
      <div style={styles.header}>
        <div style={styles.titleRow}>
          <h1 style={styles.title}>Panoramica</h1>
          <Badge variant={isAdmin ? 'gold' : 'info'}>
            {isAdmin ? 'Amministratore' : 'Organizzatore'}
          </Badge>
        </div>
        <p style={styles.subtitle}>
          {isAdmin ? 'Panoramica generale del gioco' : 'Le tue statistiche'}
        </p>
      </div>

      <div style={styles.statsGrid}>
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          const value = stats[stat.key] ?? 0;

          return (
            <motion.div
              key={stat.key}
              style={styles.statCard}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.25)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div
                style={{
                  ...styles.statIcon,
                  backgroundColor: stat.bg,
                  color: stat.color,
                }}
              >
                <Icon size={24} />
              </div>
              <div style={styles.statContent}>
                <p style={styles.statLabel}>{stat.label}</p>
                <p style={styles.statValue}>{formatValue(value, stat.format)}</p>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Admin: global dashboard stats */}
      {isAdmin && adminData && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Statistiche globali</h2>
          <div style={styles.summaryGrid}>
            <div style={styles.summaryCard}>
              <p style={styles.summaryLabel}>Totale organizzatori</p>
              <p style={styles.summaryValue}>
                {globalStats.total_operators ?? globalStats.total_users ?? 0}
              </p>
            </div>
            <div style={styles.summaryCard}>
              <p style={styles.summaryLabel}>Partite globali</p>
              <p style={styles.summaryValue}>
                {globalStats.total_games ?? 0}
              </p>
            </div>
            <div style={styles.summaryCard}>
              <p style={styles.summaryLabel}>Partite completate (globale)</p>
              <p style={styles.summaryValue}>
                {globalStats.completed_games ?? 0}
              </p>
            </div>
          </div>

          <div style={{ ...styles.summaryGrid, marginTop: 'var(--space-lg)' }}>
            <Link
              to="/admin/users"
              style={styles.adminCard}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.25)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={styles.adminCardIcon}>
                <Users size={24} />
              </div>
              <div style={styles.adminCardContent}>
                <p style={styles.adminCardTitle}>Gestione organizzatori</p>
                <p style={styles.adminCardDesc}>Gestisci organizzatori e amministratori</p>
              </div>
            </Link>
          </div>
        </div>
      )}

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Riepilogo</h2>
        <div style={styles.summaryGrid}>
          <div style={styles.summaryCard}>
            <p style={styles.summaryLabel}>Premio più alto</p>
            <p style={styles.summaryValue}>
              {formatValue(stats.max_prize, 'currency')}
            </p>
          </div>

          <div style={styles.summaryCard}>
            <p style={styles.summaryLabel}>Offerte accettate</p>
            <p style={styles.summaryValue}>
              {stats.offers_accepted ?? 0}
            </p>
            <p style={styles.summaryDetail}>
              su {(stats.offers_accepted ?? 0) + (stats.offers_rejected ?? 0)} offerte totali
            </p>
          </div>

          <div style={styles.summaryCard}>
            <p style={styles.summaryLabel}>Offerte rifiutate</p>
            <p style={styles.summaryValue}>
              {stats.offers_rejected ?? 0}
            </p>
            {stats.completed_games > 0 && (
              <p style={styles.summaryDetail}>
                {Math.round(((stats.offers_rejected ?? 0) / stats.completed_games) * 100)}% delle partite completate
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
